import React, { useState, useRef } from 'react'
import { ObsidianGlassCard } from '../obsidian/ObsidianGlassCard'
import { PillButton } from '../actions/PillButton'
import { BadgeChip } from '../editorial/BadgeChip'

export interface DropZoneStudioProps {
  onFilesSelected: (files: File[]) => void
  isProcessing: boolean
}

export const DropZoneStudio: React.FC<DropZoneStudioProps> = ({
  onFilesSelected,
  isProcessing,
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files)
      onFilesSelected(files)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files)
      onFilesSelected(files)
      // Reset input value so same files can be re-selected if needed
      e.target.value = ''
    }
  }

  return (
    <ObsidianGlassCard
      variant={isDragOver ? 'solution' : 'default'}
      style={{
        marginBottom: '32px',
        cursor: 'pointer',
        transition: 'all 250ms ease',
      }}
      innerStyle={{
        padding: '44px 24px',
        textAlign: 'center',
        background: isDragOver
          ? 'radial-gradient(ellipse at center, rgba(42, 230, 182, 0.08) 0%, #070A11 75%)'
          : 'var(--surface-obsidian-card, #070A11)',
        border: isDragOver
          ? '1.5px dashed var(--c-pulse, #2AE6B6)'
          : '1.5px dashed rgba(255, 255, 255, 0.12)',
        borderRadius: '24px',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/avif,image/svg+xml,.svg,video/mp4,video/quicktime,video/webm,.mov,.mkv"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: isDragOver
            ? 'rgba(42, 230, 182, 0.15)'
            : 'rgba(56, 191, 255, 0.08)',
          border: `1px solid ${
            isDragOver ? 'var(--c-pulse, #2AE6B6)' : 'rgba(56, 191, 255, 0.25)'
          }`,
          boxShadow: isDragOver
            ? '0 0 30px rgba(42, 230, 182, 0.3)'
            : '0 0 20px rgba(56, 191, 255, 0.1)',
          marginBottom: '20px',
          transition: 'all 250ms ease',
        }}
      >
        <i
          className={`pi ${isDragOver ? 'pi-arrow-circle-down' : 'pi-images'}`}
          style={{
            fontSize: '30px',
            color: isDragOver ? 'var(--c-pulse, #2AE6B6)' : 'var(--c-accent-electric, #38BFFF)',
          }}
        />
      </div>

      <h2
        style={{
          fontFamily: "var(--font-display, 'Unbounded', sans-serif)",
          fontSize: 'clamp(20px, 3vw, 26px)',
          fontWeight: 700,
          color: '#ffffff',
          letterSpacing: '-0.02em',
          marginBottom: '10px',
          lineHeight: 1.25,
        }}
      >
        El medio primero.{' '}
        <span style={{ color: 'var(--c-accent-electric, #38BFFF)' }}>
          La compresión, invisible.
        </span>
      </h2>

      <p
        style={{
          fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)",
          fontSize: '15px',
          color: 'rgba(255, 255, 255, 0.65)',
          maxWidth: '560px',
          margin: '0 auto 24px auto',
          lineHeight: 1.5,
        }}
      >
        Arrastra imágenes, vectores SVG o clips de video (MP4, MOV, WebM). Motor híbrido
        de alta velocidad con aceleración por hardware GPU y nube serverless.
      </p>

      <div style={{ marginBottom: '24px' }}>
        <PillButton
          label={isProcessing ? 'Procesando en lote...' : 'Seleccionar Archivos'}
          variant="solid"
          size="lg"
          icon="pi-upload"
          badgeIcon="pi-arrow-right"
          loading={isProcessing}
          onClick={(e) => {
            e.stopPropagation()
            inputRef.current?.click()
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <BadgeChip variant="electric">PNG/JPEG</BadgeChip>
        <BadgeChip variant="electric">AVIF/WEBP</BadgeChip>
        <BadgeChip variant="electric">MP4/MOV</BadgeChip>
        <BadgeChip variant="pulse" icon="pi-video">
          VIDEO HÍBRIDO (GPU + CLOUD)
        </BadgeChip>
        <BadgeChip variant="pulse" icon="pi-star">
          FAVICON SUITE
        </BadgeChip>
      </div>
    </ObsidianGlassCard>
  )
}
